import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Swal from "sweetalert2";
import FlowEngineGlobalGate from "../specialist-flow-engine/FlowEngineGlobalGate";
import {
  startDeleteFlowChecklistDef,
  startLoadingFlowChecklistDefs,
  startRegisterFlowChecklistDef,
  startUpdateFlowChecklistDef
} from "../../app/actions/flowChecklistActions";
import { startLoadingCatalogModules } from "../../app/actions/catalogModuleActions";
import { getCatalogModuleOptionLabel, getModuleDisplayLabelById } from "../../app/helpers/flowEngineConfigHelper";

const ROLE_OPTIONS = [
  { id: 3, labelKey: "admin.flowModule.diagram.role.feeder", fallback: "Alimentador" },
  { id: 4, labelKey: "admin.flowModule.diagram.role.pre-manager", fallback: "Pre-Gestor" },
  { id: 5, labelKey: "admin.flowModule.diagram.role.manager", fallback: "Gestor" },
  { id: 6, labelKey: "admin.flowModule.diagram.role.validator", fallback: "Verificador" },
  { id: 7, labelKey: "admin.flowModule.diagram.role.editor", fallback: "Editor" }
];

const AdminFlowChecklistScreen = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { defs, loading } = useSelector((s) => s.flowChecklist);
  const { catalogModules = [] } = useSelector((s) => s.catalogModule);
  const [form, setForm] = useState({
    catalogmodule_id: "",
    catalogrole_id: 5,
    title: "",
    i18n_key: "",
    activity_title: "",
    item_type: "SUB",
    activity_order: 1,
    sequence_order: 1,
    required_to_advance: true,
    enabled: true
  });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    dispatch(startLoadingCatalogModules());
    dispatch(startLoadingFlowChecklistDefs());
  }, [dispatch]);

  const resetForm = () => {
    setEditingId(null);
    setForm({
      catalogmodule_id: "",
      catalogrole_id: 5,
      title: "",
      i18n_key: "",
      activity_title: "",
      item_type: "SUB",
      activity_order: 1,
      sequence_order: 1,
      required_to_advance: true,
      enabled: true
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      ...form,
      catalogmodule_id: Number(form.catalogmodule_id),
      catalogrole_id: Number(form.catalogrole_id),
      activity_order: Number(form.activity_order || 0),
      sequence_order: Number(form.sequence_order || 0),
      item_type: form.item_type,
      activity_title: form.activity_title || form.title
    };
    if (editingId) {
      await dispatch(startUpdateFlowChecklistDef(editingId, payload));
    } else {
      await dispatch(startRegisterFlowChecklistDef(payload));
    }
    resetForm();
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: t("flow.checklist.confirm-delete", "¿Desactivar esta actividad?"),
      icon: "warning",
      showCancelButton: true
    });
    if (result.isConfirmed) {
      await dispatch(startDeleteFlowChecklistDef(id));
    }
  };

  return (
    <FlowEngineGlobalGate>
      <div className="container-fluid py-3">
        <h3 className="mb-3">{t("admin.flowChecklist.title", "Checklist de actividades")}</h3>
        <form className="row g-2 align-items-end mb-4" onSubmit={handleSubmit}>
          <div className="col-md-3">
            <label className="form-label">{t("admin.flowChecklist.module", "Módulo")}</label>
            <select className="form-select" required value={form.catalogmodule_id} onChange={(e) => setForm({ ...form, catalogmodule_id: e.target.value })}>
              <option value="">{t("general.option_search")}</option>
              {catalogModules.map((mod) => (
                <option key={mod.id} value={mod.id}>{getCatalogModuleOptionLabel(mod, t)}</option>
              ))}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">{t("admin.flowChecklist.tray", "Bandeja")}</label>
            <select className="form-select" value={form.catalogrole_id} onChange={(e) => setForm({ ...form, catalogrole_id: Number(e.target.value) })}>
              {ROLE_OPTIONS.map((role) => <option key={role.id} value={role.id}>{t(role.labelKey, role.fallback)}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <label className="form-label">{t("admin.flowChecklist.type", "Tipo")}</label>
            <select className="form-select" value={form.item_type} onChange={(e) => setForm({ ...form, item_type: e.target.value })}>
              <option value="ACT">ACT — Actividad</option>
              <option value="SUB">SUB — Subactividad</option>
            </select>
          </div>
          <div className="col-md-4">
            <label className="form-label">{t("admin.flowChecklist.title", "Título visible")}</label>
            <input className="form-control" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="col-md-4">
            <label className="form-label">{t("admin.flowChecklist.i18n", "Clave i18n")}</label>
            <input className="form-control" required value={form.i18n_key} onChange={(e) => setForm({ ...form, i18n_key: e.target.value })} />
          </div>
          <div className="col-md-4">
            <label className="form-label">{t("admin.flowChecklist.parent", "Actividad padre")}</label>
            <input className="form-control" value={form.activity_title} onChange={(e) => setForm({ ...form, activity_title: e.target.value })} />
          </div>
          <div className="col-md-1">
            <label className="form-label">{t("admin.flowChecklist.activity-order", "Act. #")}</label>
            <input type="number" className="form-control" value={form.activity_order} onChange={(e) => setForm({ ...form, activity_order: e.target.value })} />
          </div>
          <div className="col-md-1">
            <label className="form-label">{t("admin.flowChecklist.order", "Orden")}</label>
            <input type="number" className="form-control" value={form.sequence_order} onChange={(e) => setForm({ ...form, sequence_order: e.target.value })} />
          </div>
          <div className="col-md-2">
            <button type="submit" className="btn btn-primary w-100" disabled={loading}>
              {editingId ? t("buttons.update", "Actualizar") : t("buttons.add", "Agregar")}
            </button>
          </div>
          <div className="col-12 d-flex gap-3">
            <label className="form-check">
              <input type="checkbox" className="form-check-input" checked={form.required_to_advance} onChange={(e) => setForm({ ...form, required_to_advance: e.target.checked })} />
              <span className="form-check-label">{t("admin.flowChecklist.required", "Requerida para avanzar")}</span>
            </label>
            <label className="form-check">
              <input type="checkbox" className="form-check-input" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
              <span className="form-check-label">{t("admin.flowChecklist.enabled", "Activa")}</span>
            </label>
          </div>
        </form>

        <div className="table-responsive">
          <table className="table table-sm">
            <thead>
              <tr>
                <th>ID</th>
                <th>{t("admin.flowChecklist.module", "Módulo")}</th>
                <th>{t("admin.flowChecklist.tray", "Bandeja")}</th>
                <th>{t("admin.flowChecklist.type", "Tipo")}</th>
                <th>{t("admin.flowChecklist.parent", "Actividad")}</th>
                <th>{t("admin.flowChecklist.title", "Título")}</th>
                <th>{t("admin.flowChecklist.i18n", "Clave i18n")}</th>
                <th>{t("admin.flowChecklist.required", "Requerida")}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {(defs || []).map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td>{getModuleDisplayLabelById(row.catalogmodule_id, t, String(row.catalogmodule_id))}</td>
                  <td>{(() => {
                    const role = ROLE_OPTIONS.find((item) => Number(item.id) === Number(row.catalogrole_id));
                    return role ? t(role.labelKey, role.fallback) : row.catalogrole_id;
                  })()}</td>
                  <td>{row.item_type || "SUB"}</td>
                  <td>{row.activity_title || row.title}</td>
                  <td>{row.title}</td>
                  <td>{row.i18n_key || "—"}</td>
                  <td>{row.required_to_advance ? t("general.yes", "Sí") : t("general.no", "No")}</td>
                  <td className="text-end">
                    <button type="button" className="btn btn-sm btn-outline-secondary me-2" onClick={() => {
                      setEditingId(row.id);
                      setForm({
                        catalogmodule_id: row.catalogmodule_id,
                        catalogrole_id: row.catalogrole_id,
                        title: row.title,
                        i18n_key: row.i18n_key || "",
                        activity_title: row.activity_title || row.title,
                        item_type: row.item_type || "SUB",
                        activity_order: row.activity_order || 1,
                        sequence_order: row.sequence_order,
                        required_to_advance: !!row.required_to_advance,
                        enabled: row.enabled !== false
                      });
                    }}>{t("buttons.edit", "Editar")}</button>
                    <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleDelete(row.id)}>{t("buttons.delete", "Eliminar")}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </FlowEngineGlobalGate>
  );
};

export default AdminFlowChecklistScreen;
